import { FC, useEffect, useRef } from 'react';

import Dinero from 'dinero.js';
import { useFeatureFlag } from 'context/FeatureFlagContext';
import { isUnsecEnabled } from 'features/flags';
import { GET_PORTFOLIO_SUMMARY_QUERY, kpiConfigs } from 'features/pages/portfolio/PortfolioSummary';
import { useTourContext } from 'tours/TourProvider';

import { useQuery } from '@apollo/client';

import { AssetClass } from '__generated__/globalTypes';

import { GetPools } from 'query/__generated__/GetPools';
import { GET_POOLS } from 'query/getPools';

import {
  GetPortfolioSummary,
  GetPortfolioSummaryVariables,
} from '../__generated__/GetPortfolioSummary';
import { PortfolioLayout } from '../Portfolio/PortfolioLayout';

const usePortfolioSummary = (assetClass?: AssetClass, skipCondition?: boolean) => {
  const { data, loading } = useQuery<GetPortfolioSummary, GetPortfolioSummaryVariables>(
    GET_PORTFOLIO_SUMMARY_QUERY,
    {
      variables: {
        hasAssetClass: !!assetClass,
        assetClass,
      },
      skip: skipCondition,
      fetchPolicy: 'cache-and-network',
      pollInterval: 15000,
    },
  );
  return { data, loading };
};

type KpiConfig = {
  description: string;
  selector: string;
  display: (value: number) => string;
};

type KpiWithValue = KpiConfig & {
  value: string;
};

const mapKpis = (data: GetPortfolioSummary | undefined): KpiWithValue[] => {
  return kpiConfigs.map((kpi) => {
    if (!data) {
      return {
        ...kpi,
        value: '-',
      };
    }

    const performanceData =
      data.user.company.assetClassPerformanceData || data.user.company.performanceData;
    const value = performanceData ? (performanceData[kpi.selector] ?? 0) : 0;
    return {
      ...kpi,
      value: kpi.description === 'UPB' ? formatUpb(value) : kpi.display(value),
    };
  });
};

const formatUpb = (value: number) => {
  const truncatedValue = Math.floor(value);
  return Dinero({
    amount: truncatedValue,
    currency: 'USD',
  }).toFormat('$0,0');
};

const KpiCardContainer = ({
  kpis,
  'data-tour': dataTour,
  'data-testid-prefix': testIdPrefix,
}: {
  kpis: KpiWithValue[];
  'data-tour'?: string;
  'data-testid-prefix'?: string;
}) => {
  const labelToSlug = (label: string) =>
    label === '#'
      ? 'loans'
      : label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
  return (
    <div className="flex flex-row gap-4" data-tour={dataTour}>
      <div
        className="flex h-16 min-w-48 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-[#260626] bg-[#e028dd] bg-gradient-to-r from-[#1555ec] to-[#ff23e1] p-2 text-center"
        data-testid={
          testIdPrefix ? `${testIdPrefix}-${labelToSlug(kpis[0].description)}` : undefined
        }
      >
        <div className="font-mono text-xl text-gray-100">{kpis[0].value}</div>
        <div>{kpis[0].description === '#' ? '# LOANS' : kpis[0].description}</div>
      </div>
      {kpis.slice(1).map((kpi) => (
        <div
          key={kpi.description}
          className="flex h-16 min-w-32 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-gray-500 bg-gray-800 p-2 text-center"
          data-testid={testIdPrefix ? `${testIdPrefix}-${labelToSlug(kpi.description)}` : undefined}
        >
          <div className="font-mono text-xl text-gray-100">{kpi.value}</div>
          <div>{kpi.description === '#' ? '# LOANS' : kpi.description}</div>
        </div>
      ))}
    </div>
  );
};

type AssetClassSectionProps = {
  title: string;
  subtitle?: string;
  poolsCount: number;
  kpis: KpiWithValue[];
  poolsDataTour?: string;
  testIdPrefix?: string;
};

const AssetClassSection: FC<AssetClassSectionProps> = ({
  title,
  subtitle,
  poolsCount,
  kpis,
  poolsDataTour,
  testIdPrefix,
}) => (
  <div
    className="flex flex-col gap-5"
    data-testid={testIdPrefix ? `${testIdPrefix}-section` : undefined}
  >
    <div className="flex flex-col gap-1">
      <h1 className="text-3xl font-bold text-white">{title}</h1>
      {subtitle && <h2 className="text-2xl font-bold text-gray-400">{subtitle}</h2>}
      <div
        className="text-lg text-gray-400"
        data-tour={poolsDataTour}
        data-testid={testIdPrefix ? `${testIdPrefix}-pools-count` : undefined}
      >
        {poolsCount} pools
      </div>
    </div>
    <KpiCardContainer kpis={kpis} data-testid-prefix={testIdPrefix} />
  </div>
);

const PortfolioSkeleton: FC = () => {
  return (
    <div className="flex w-full justify-center overflow-x-auto">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 py-8">
        {/* Total section skeleton */}
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <div className="h-8 w-60 animate-pulse rounded bg-gray-700 opacity-50"></div>
            <div className="h-6 w-20 animate-pulse rounded bg-gray-600 opacity-50"></div>
          </div>
          <div className="flex flex-row gap-4">
            <div className="flex h-16 min-w-48 shrink-0 animate-pulse flex-col items-center justify-center gap-1 rounded-lg border border-[#260626] bg-gradient-to-r from-[#1555ec] to-[#ff23e1] p-2 opacity-50">
              <div className="h-6 w-24 rounded bg-gray-600"></div>
              <div className="mt-2 h-4 w-16 rounded bg-gray-500"></div>
            </div>
            {[...Array(7)].map((_, idx) => (
              <div
                key={idx}
                className="flex h-16 min-w-32 shrink-0 animate-pulse flex-col items-center justify-center gap-1 rounded-lg border border-gray-500 bg-gray-700 p-2 opacity-50"
              >
                <div className="h-6 w-24 rounded bg-gray-600"></div>
                <div className="mt-2 h-4 w-16 rounded bg-gray-500"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Asset class sections skeleton */}
        {[...Array(3)].map((_, idx) => (
          <div key={idx} className="flex flex-col gap-5">
            <div className="flex flex-col gap-1">
              <div className="h-9 w-32 animate-pulse rounded bg-gray-700 opacity-50"></div>
              <div className="h-7 w-48 animate-pulse rounded bg-gray-600 opacity-50"></div>
              <div className="h-6 w-20 animate-pulse rounded bg-gray-600 opacity-50"></div>
            </div>
            <div className="flex flex-row gap-4">
              <div className="flex h-16 min-w-48 shrink-0 animate-pulse flex-col items-center justify-center gap-1 rounded-lg border border-[#260626] bg-gradient-to-r from-[#1555ec] to-[#ff23e1] p-2 opacity-50">
                <div className="h-6 w-24 rounded bg-gray-600"></div>
                <div className="mt-2 h-4 w-16 rounded bg-gray-500"></div>
              </div>
              {[...Array(7)].map((_, cardIdx) => (
                <div
                  key={cardIdx}
                  className="flex h-16 min-w-32 shrink-0 animate-pulse flex-col items-center justify-center gap-1 rounded-lg border border-gray-500 bg-gray-700 p-2 opacity-50"
                >
                  <div className="h-6 w-24 rounded bg-gray-600"></div>
                  <div className="mt-2 h-4 w-16 rounded bg-gray-500"></div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PortfolioDashboard = () => {
  const isCreEnabled = useFeatureFlag('creEnabled');
  const { startTour, tourDataLoading } = useTourContext();
  const hasStartedTours = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTourRef = useRef(startTour);
  const retryAttemptsRef = useRef(0);

  const { data, loading } = usePortfolioSummary();
  const { data: autoData, loading: autoLoading } = usePortfolioSummary(AssetClass.AUTO);
  const { data: homeData, loading: homeLoading } = usePortfolioSummary(AssetClass.HOME);
  const { data: unsecData, loading: unsecLoading } = usePortfolioSummary(
    AssetClass.UNSECPERSONAL,
    !isUnsecEnabled(),
  );
  const { data: creData, loading: creLoading } = usePortfolioSummary(AssetClass.CRE, !isCreEnabled);
  const { data: poolsData } = useQuery<GetPools>(GET_POOLS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 15000,
  });

  const kpis = mapKpis(data);
  const autoKpis = mapKpis(autoData);
  const homeKpis = mapKpis(homeData);
  const creKpis = mapKpis(creData);

  const autoPools =
    poolsData?.user.company.pools.filter((listing) => listing.asset_class === AssetClass.AUTO)
      .length || 0;
  const homePools =
    poolsData?.user.company.pools.filter((listing) => listing.asset_class === AssetClass.HOME)
      .length || 0;
  const unsecPools =
    poolsData?.user.company.pools.filter(
      (listing) => listing.asset_class === AssetClass.UNSECPERSONAL,
    ).length || 0;
  const crePools =
    poolsData?.user.company.pools.filter((listing) => listing.asset_class === AssetClass.CRE)
      .length || 0;

  const totalPools = autoPools + homePools + (isCreEnabled ? crePools : 0);

  const isLoading =
    loading ||
    autoLoading ||
    homeLoading ||
    (isUnsecEnabled() && unsecLoading) ||
    (isCreEnabled && creLoading);

  useEffect(() => {
    startTourRef.current = startTour;
  }, [startTour]);

  useEffect(() => {
    if (hasStartedTours.current) {
      return;
    }

    if (tourDataLoading || isLoading) {
      return;
    }

    const checkTourElementsReady = (): boolean => {
      const firstElement = document.querySelector('[data-tour="dashboard-total-kpis"]');
      return firstElement !== null;
    };

    const attemptStartTour = () => {
      if (hasStartedTours.current || tourDataLoading || isLoading) {
        return;
      }

      if (checkTourElementsReady()) {
        retryAttemptsRef.current = 0;
        timeoutRef.current = setTimeout(() => {
          if (hasStartedTours.current || tourDataLoading || isLoading) {
            return;
          }
          hasStartedTours.current = true;
          startTourRef.current('portfolio.dashboard');
          timeoutRef.current = null;
        }, 300);
      } else if (retryAttemptsRef.current < 20) {
        retryAttemptsRef.current += 1;
        timeoutRef.current = setTimeout(attemptStartTour, 150);
      } else {
        console.warn('[Tour] Dashboard tour elements not found after max retries');
      }
    };

    attemptStartTour();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [tourDataLoading, isLoading]);

  return (
    <PortfolioLayout hideSummery={true}>
      {isLoading ? (
        <PortfolioSkeleton />
      ) : (
        <div className="flex w-full justify-center overflow-x-auto">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 py-8">
            <div
              className="flex flex-col gap-5"
              data-tour="dashboard-total-kpis"
              data-testid="portfolio-dashboard-total-section"
            >
              <div className="flex flex-col gap-1">
                <h2 className="text-2xl font-bold text-white">Total in your Portfolio</h2>
                <div
                  className="text-lg text-gray-400"
                  data-testid="portfolio-dashboard-total-pools-count"
                >
                  {totalPools} pools
                </div>
              </div>
              <KpiCardContainer kpis={kpis} data-testid-prefix="portfolio-dashboard-total" />
            </div>

            <AssetClassSection
              title="Auto"
              subtitle="Total by asset class"
              poolsCount={autoPools}
              kpis={autoKpis}
              testIdPrefix="portfolio-dashboard-auto"
            />

            <AssetClassSection
              title="Resi"
              subtitle="Total by asset class"
              poolsCount={homePools}
              kpis={homeKpis}
              poolsDataTour="dashboard-resi-pools"
            />

            {isCreEnabled && (
              <AssetClassSection
                title="CRE"
                subtitle="Total by asset class"
                poolsCount={crePools}
                kpis={creKpis}
              />
            )}

            {isUnsecEnabled() && (
              <AssetClassSection
                title="Unsecured Personal"
                subtitle="Total by asset class"
                poolsCount={unsecPools}
                kpis={mapKpis(unsecData)}
              />
            )}
          </div>
        </div>
      )}
    </PortfolioLayout>
  );
};

export default PortfolioDashboard;
